import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Heading, SimpleGrid, Card, CardHeader, CardBody, Text, Spinner, useToast, VStack, HStack, Divider, Container, Button, ButtonGroup
} from '@chakra-ui/react';
import axios from 'axios';

const API_BASE_URL = 'https://api-amg2dwk5ga-uc.a.run.app';

// Reusable component for displaying each detail item cleanly
const ProfileDetail = ({ label, value }) => (
  <Box>
    <Text fontSize="sm" color="gray.600">{label}</Text>
    <Text fontSize="md" fontWeight="medium">{value || 'N/A'}</Text>
  </Box>
);

const MatrimonyList = () => {
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [genderFilter, setGenderFilter] = useState('All'); // 1. State for the filter
  const toast = useToast();

  useEffect(() => {
    const fetchProfiles = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/matrimony/profiles`);
        setProfiles(response.data);
      } catch (error) {
        toast({ 
            title: 'Error fetching profiles', 
            status: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfiles();
  }, [toast]);

  // 2. Filter profiles based on the selected gender
  const filteredProfiles = useMemo(() => {
    if (genderFilter === 'All') {
      return profiles;
    }
    return profiles.filter(
      (profile) => profile.gender?.toLowerCase() === genderFilter.toLowerCase()
    );
  }, [profiles, genderFilter]);

  if (isLoading) {
    return (
      <VStack justify="center" align="center" h="50vh">
        <Spinner size="xl" />
        <Text>Loading Matrimonial Profiles...</Text>
      </VStack>
    );
  }

  return (
    <Container maxW="container.xl" py={12}>
      <VStack spacing={8}>
        <Heading textAlign="center">Matrimonial Profiles</Heading>
        
        {/* 3. Filter buttons UI */}
        <ButtonGroup variant="outline" spacing="6">
          <Button
            colorScheme={genderFilter === 'All' ? 'brand' : 'gray'}
            onClick={() => setGenderFilter('All')}
          >
            Show All
          </Button>
          <Button
            colorScheme={genderFilter === 'Male' ? 'brand' : 'gray'}
            onClick={() => setGenderFilter('Male')}
          >
            Male Profiles
          </Button>
          <Button
            colorScheme={genderFilter === 'Female' ? 'brand' : 'gray'}
            onClick={() => setGenderFilter('Female')}
          >
            Female Profiles
          </Button>
        </ButtonGroup>

        {filteredProfiles.length === 0 ? (
          <Text textAlign="center" fontSize="lg" color="gray.500" pt={10}>
              There are no profiles to display for the selected filter.
          </Text>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} w="full">
            {/* 4. Map over the new filteredProfiles array */}
            {filteredProfiles.map((profile) => (
              // --- 1. ADDED WRAPPER BOX ---
              // This Box holds the card and the overlay elements
              <Box
                key={profile.id}
                position="relative"
                overflow="hidden"
                borderRadius="lg"
                boxShadow="md"
              >
                {/* --- 2. ADDED CONDITIONAL OVERLAY & STAMP --- */}
                {profile.is_married && (
                  <>
                    {/* The "Just Married" Stamp */}
                    <Box
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%) rotate(-15deg)"
                      zIndex={10}
                      bg="red.500"
                      color="white"
                      fontWeight="bold"
                      fontSize="xl" // Slightly smaller font
                      fontFamily="serif"
                      textAlign="center"
                      w="140px" // Slightly smaller stamp
                      h="140px"
                      borderRadius="full"
                      border="3px solid white"
                      boxShadow="lg"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      textTransform="uppercase"
                      opacity={0.85}
                      p={3}
                    >
                      Happily Matched
                    </Box>
                    {/* The Dimming Overlay */}
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      bg="whiteAlpha.700" // White transparent overlay
                      zIndex={9}
                      cursor="not-allowed"
                    />
                  </>
                )}
              <Card
                  borderWidth="1px"
                  borderRadius="lg"
                  overflow="hidden"
                  // Apply grayscale filter and reduced opacity
                  filter={profile.is_married ? 'grayscale(100%)' : 'none'}
                  opacity={profile.is_married ? 0.6 : 1}
                  transition="all 0.2s" // Added subtle transition
                >
                  <CardHeader bg="gray.100" py={3}>
                    <Heading size="md">Profile ID: {profile.member_id}</Heading>
                  </CardHeader>
                  <Divider />
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      {/* Your existing layout is preserved */}
                      <SimpleGrid columns={2} spacing={4}>
                        <ProfileDetail label="Date of Birth" value={profile.date_of_birth} />
                        <ProfileDetail label="Height" value={profile.height} />
                        <ProfileDetail label="Complexion" value={profile.complexion} />
                        <ProfileDetail label="Gotra" value={profile.gotra} />
                      </SimpleGrid>
                      <Divider />
                      <ProfileDetail label="Education" value={profile.education} />
                      <ProfileDetail label="Occupation" value={profile.occupation} />
                      <ProfileDetail label="Current Location" value={profile.current_location} />
                      <ProfileDetail label="Annual Income" value={profile.income} />
                      <ProfileDetail label="Work Location" value={profile.work_location} />
                      <Divider />
                      <Heading size="sm" mt={2}>Family Details</Heading>
                      <SimpleGrid columns={2} spacing={4}>
                        <ProfileDetail label="Father's Occupation" value={profile.father_occupation} />
                        <ProfileDetail label="Mother's Gotra" value={profile.mother_gotra} />
                        <ProfileDetail label="Siblings" value={profile.siblings} />
                      </SimpleGrid>
                      {profile.preferences && (
                        <>
                          <Divider />
                          <ProfileDetail label="Partner Preferences" value={profile.preferences} />
                        </>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </VStack>
    </Container>
  );
};

export default MatrimonyList;